import React from 'react';
import dayjs from 'dayjs';
import { ExpandableParagraph, Panel } from 'components/molecules';
import { Divider, Section, Title } from '../../ThirdPartyProfile.styles';
import { InternalNotes, GeneralPanels } from './Profile.styles';
import { Date as DateComponent, DescriptionList, Map, Typography } from 'components/atoms';
import { GatewayPermissions } from 'auth/constants/permissions';
import { useCountryHelper } from 'helpers/countriesHelper';
import { useUserSettings, useTerminologyConfig } from 'context/DataContext';
import { ThirdPartyModelResponse, ThirdPartyType } from 'types';
import { ThirdPartyDataConfigDictionary } from 'services/getThirdPartyConfiguration';
import { ApprovalStatusPanel } from './ApprovalStatusPanel';
import { ThirdPartyRouteInfo } from 'types/router';

type Props = {
  id: string;
  profile: ThirdPartyModelResponse;
  thirdPartyDataConfig?: ThirdPartyDataConfigDictionary;
  routeState: {
    mainThirdParty: ThirdPartyRouteInfo;
    linkedThirdParty: ThirdPartyRouteInfo;
  };
};

const Profile = ({ id, profile, thirdPartyDataConfig, routeState }: Props) => {
  const { hasPermission } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const getOwnerName = (position: number) => {
    const owner = profile?.thirdPartyOwners?.[position]?.users;
    const items = [owner?.firstName, owner?.lastName].filter(Boolean);
    let name = items.length > 0 ? items.join(' ') : '-';
    if (owner?.company) {
      name += ` (${owner.company})`;
    }
    return name;
  };
  const { getName } = useCountryHelper();

  const getAddress = () => {
    const items = [profile?.addressLineOne, profile?.city, profile?.zipCode].filter(Boolean);
    return items.length > 0 ? items.join(', ') : '-';
  };
  const lastThirdPartyApproval = profile.thirdPartyApproval?.sort(
    (a, b) => dayjs(b.reviewDate!).toDate().getTime() - dayjs(a.reviewDate!).toDate().getTime()
  )[0];

  const getNextReviewDate = () => {
    return profile?.nextReviewDate ? <DateComponent dateTime={profile?.nextReviewDate} /> : null;
  };
  const creationMeta = () => {
    const base = [
      {
        key: 'Date created',
        value: dayjs(profile?.createdDate).format('Do MMMM, YYYY'),
      },
      {
        key: 'Created by',
        value: profile?.createdBy,
      },
    ];
    if (profile?.updatedDate) {
      base.push({
        key: 'Date last modified',
        value: dayjs(profile?.updatedDate).format('Do MMMM, YYYY'),
      });
    }
    if (profile?.updatedBy) {
      base.push({ key: 'Modified by', value: profile?.updatedBy });
    }
    return base;
  };

  const getCTA = (isAllowed, link, routeState) =>
    isAllowed ? { pathname: link, state: routeState } : null;

  const editLink = `/third-party/edit/${id}`;
  const editInternalOwners = `/third-party/${id}/internal-owners/edit`;
  const hasEditPermission = hasPermission(GatewayPermissions.EditThirdParty);
  return (
    <Section>
      <Title>
        {profile?.primaryCountry && <Map countryCode={profile.primaryCountry} />}
        {(profile?.name || profile?.alternativeName) && (
          <Typography tag="h2" value={profile?.name || profile?.alternativeName} />
        )}
      </Title>

      <Divider />

      <ApprovalStatusPanel
        cta={getCTA(
          hasPermission(GatewayPermissions.ApproveRejectThirdParty),
          `/third-party/edit-approval/${id}`,
          routeState
        )}
        approvalComment={lastThirdPartyApproval?.comments}
        reviewDate={lastThirdPartyApproval?.reviewDate}
        approvalStatus={lastThirdPartyApproval?.approvalStatus}
        riskRating={lastThirdPartyApproval?.riskRating}
      />
      <GeneralPanels>
        <Panel cta={getCTA(hasEditPermission, editLink, routeState)} icon="edit-pencil">
          <DescriptionList
            items={[
              { key: 'Name', value: profile?.name || '-' },
              {
                key: 'Alternative names',
                value: profile?.alternativeName || '-',
              },
              {
                key: `${terminologyConfig?.internalIdSingularNaming}`,
                value: profile?.internalId || '-',
              },
              {
                key: 'Next review date',
                value: getNextReviewDate(),
              },
              { key: 'Address', value: getAddress() },
              {
                key: thirdPartyDataConfig?.primaryCountry?.customName,
                value: profile?.primaryCountry ? getName(profile?.primaryCountry) : '-',
              },
              {
                key: 'Country 2',
                value: profile?.countryTwo ? getName(profile?.countryTwo) : '-',
              },
              {
                key: 'Country 3',
                value: profile?.countryThree ? getName(profile?.countryThree) : '-',
              },
              ...(profile?.type === ThirdPartyType.Individual
                ? [
                    {
                      key: 'Passport number',
                      value: profile?.passportNumber || '-',
                    },
                    {
                      key: 'National ID number',
                      value: profile?.nationalIDNumber || '-',
                    },
                    {
                      key: 'Other ID number',
                      value: profile?.otherIDNumber || '-',
                    },
                  ]
                : profile?.type === ThirdPartyType.Business
                ? [
                    { key: 'DUNS', value: profile?.duns || '-' },
                    {
                      key: 'Registration number',
                      value: profile?.registrationNumber || '-',
                    },
                    { key: 'SWIFT BIC', value: profile?.swift || '-' },
                    {
                      key: 'Company Website',
                      value: profile?.companyWebsite || '-',
                    },
                  ]
                : []),
            ]}
            spaced
            truncateText
          />
        </Panel>
        <Panel cta={getCTA(hasEditPermission, editInternalOwners, routeState)} icon="edit-pencil">
          <DescriptionList
            items={[
              {
                key: `${terminologyConfig?.internalOwnerSingularNaming}`,
                value: getOwnerName(0),
              },
              {
                key: `${terminologyConfig?.internalOwnerSingularNaming}`,
                value: getOwnerName(1),
              },
              {
                key: `${terminologyConfig?.internalOwnerSingularNaming}`,
                value: getOwnerName(2),
              },
            ]}
            spaced
          />
        </Panel>
        <Panel cta={getCTA(hasEditPermission, editLink, routeState)} icon="edit-pencil">
          <DescriptionList
            items={[
              { key: 'Key contact', value: profile?.keyFullName || '-' },
              {
                key: 'Key contact number',
                value: profile?.keyPhoneNumber || '-',
              },
              { key: 'Key contact email', value: profile?.keyEmail || '-' },
            ]}
            spaced
          />
        </Panel>
        <Panel cta={getCTA(hasEditPermission, editLink, routeState)} icon="edit-pencil">
          <DescriptionList
            detailsFlexDirection={'column'}
            items={[
              {
                key: thirdPartyDataConfig?.referenceField1.customName,
                value: profile?.referenceField1 || '-',
              },
              {
                key: thirdPartyDataConfig?.referenceField2.customName,
                value: profile?.referenceField2 || '-',
              },
              {
                key: thirdPartyDataConfig?.referenceField3.customName,
                value: profile?.referenceField3 || '-',
              },
            ]}
            spaced
          />
        </Panel>
        <Panel cta={getCTA(hasEditPermission, editLink, routeState)} icon="edit-pencil">
          <InternalNotes>
            <Typography tag="span" value="Internal notes:" />
            <ExpandableParagraph text={profile?.internalNotes} />
          </InternalNotes>
        </Panel>
        <Panel>
          <DescriptionList items={creationMeta()} spaced />
        </Panel>
      </GeneralPanels>
    </Section>
  );
};

export default Profile;
